/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2021 Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Component, { type ComponentAttrs } from 'flarum/common/Component';
import icon from 'flarum/common/helpers/icon';
import Stream from 'flarum/common/utils/Stream';
import type Mithril from 'mithril';

export interface ExpandableContentAttrs extends ComponentAttrs {
	body?: Mithril.Children;
	footer?: Mithril.Children;
	isExpandable?: boolean;
	/** Height in pixels below which the content is always shown expanded. (sic: original attr name) */
	collapsedHeigh?: number;
}

/**
 * Not used within this extension itself; ported from GB Core for dependent extensions.
 *
 * Used by:
 * - flarum-ext-orell-articles: ArticlePage (CSS classes also used in article_page.less, LiteraryWorkPage.less and ArticleSliderItem)
 * - flarum-ext-orell-rezensionen: ReviewItem (styled in ReviewItem.less)
 */
export default class ExpandableContent extends Component<ExpandableContentAttrs> {
	expanded!: Stream<boolean>;

	oninit(vnode: Mithril.Vnode<ExpandableContentAttrs, this>) {
		super.oninit(vnode);

		this.expanded = Stream(false);
	}

	onupdate(vnode: Mithril.VnodeDOM<ExpandableContentAttrs, this>) {
		super.onupdate(vnode);

		const { collapsedHeigh } = this.attrs;

		const $body = $(this.element).find('.ExpandableContent-body');

		if (collapsedHeigh && !this.expanded() && $body.height()! < collapsedHeigh) {
			$(this.element).addClass('alwaysExpanded');
		}

		if (this.expanded()) {
			setTimeout(() => {
				$body.attr('style', `max-height: ${$body.height()}px !important`);
			}, 1000);
		} else {
			$body.attr('style', '');
		}
	}

	view() {
		const { body, footer, isExpandable } = this.attrs;

		return (
			<div className={`ExpandableContent ${this.expanded() ? 'expanded' : ''}`}>
				<div className="ExpandableContent-body">{body}</div>
				<div className="ExpandableContent-footer" onclick={this.expand.bind(this)}>
					{footer}
					{isExpandable ? icon('far fa-chevron-down') : ''}
				</div>
			</div>
		);
	}

	expand() {
		if (this.attrs.isExpandable) {
			this.expanded(!this.expanded());
		}
	}
}
